import { getAddress, isAddress } from "viem";
import type { Address } from "viem";

/** The name of a wallet's Durable Object: `chainId:address`, lower-case (D47). */
export function walletName(chainId: number, address: string): string {
  return `${chainId}:${address.toLowerCase()}`;
}

export const WALLET_NAME_RE = /^\d+:0x[0-9a-f]{40}$/;

export function parseWalletName(name: string): { chainId: number; address: Address } | null {
  if (!WALLET_NAME_RE.test(name)) return null;
  const [chain, addr] = name.split(":");
  if (!chain || !addr || !isAddress(addr)) return null;
  return { chainId: Number(chain), address: getAddress(addr) };
}

/** addr4: the first two bytes of the address, lower-case hex (`steven@68df`). */
export function addr4Of(address: string): string {
  return address.slice(2, 6).toLowerCase();
}

export function handleFor(name: string, address: string): string {
  return `${name}@${addr4Of(address)}`;
}

export interface ServiceName {
  handle: string; // steven@68df
  name: string; // steven
  addr4: string; // 68df
  service: string; // gpt
}

const SERVICE_NAME_RE = /^([a-z][a-z0-9-]{2,15})@([0-9a-f]{4})\/([a-z][a-z0-9-]{1,31})$/;

export function parseServiceName(full: string): ServiceName | null {
  const m = SERVICE_NAME_RE.exec(full);
  if (!m) return null;
  return { handle: `${m[1]}@${m[2]}`, name: m[1]!, addr4: m[2]!, service: m[3]! };
}

/** Service id rules of the deploy form: 2 to 32 characters, lowercase letters, digits and hyphens, a letter first. */
export const SERVICE_ID_RE = /^[a-z][a-z0-9-]{1,31}$/;
/** Token symbol rules: a lowercase t, a capital letter, then letters or digits; at most 17 characters. */
export const TOKEN_SYMBOL_RE = /^t[A-Z][A-Za-z0-9]{1,15}$/;

/** The first 80 characters of a message, whitespace collapsed: the title of a conversation (D33). */
export function titleFromMessage(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 80);
}

/** The account's workers.dev domain (`<account>.workers.dev`) of a Worker's origin, or null for any other host. */
export function workersDomainOf(origin: string): string | null {
  try {
    const m = /^[^.]+\.([^.]+\.workers\.dev)$/.exec(new URL(origin).hostname.toLowerCase());
    return m ? m[1]! : null;
  } catch {
    return null;
  }
}

/**
 * Whether `url` is a Worker of the same Cloudflare account on workers.dev: a host under `workersDomain`
 * (`<account>.workers.dev`). A Worker's fetch handler cannot reach such a URL (Cloudflare error 1042), so a deployed
 * gateway must never have one as its upstream; pages.dev and external hosts are fine. A URL that does not parse is
 * not one.
 */
export function sameAccountWorkersDev(url: string, workersDomain: string): boolean {
  const domain = workersDomain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/[/:].*$/, "");
  if (!domain.endsWith(".workers.dev")) return false;
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }
  return host === domain || host.endsWith(`.${domain}`);
}

/** Whether a text is an absolute http(s) URL (the deploy form's upstream field). */
export function isHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}
